/**
 * mockCaptions — deterministic captions fixture for design/QA, served while
 * the core-platform captions backend (card 868kk6pe2) is not deployed.
 *
 * Gated on the content URL carrying `?mock_captions=<variant>` (same
 * URL-param-fixture convention as Insights' `?mock=true`). It decorates the
 * fetched `video` object with the exact shape the API will ship:
 *
 *   video.natural_language  →  "pt_BR" | "en" | "es" | null
 *   video.captions          →  [{ language, status }]
 *
 * Variants:
 *   true | done  natural `en`, all tracks done — a pt_BR viewer sees the
 *                default rule auto-enable Português
 *   match        natural `pt_BR`, all done — default rule stays off
 *   partial      pt_BR done, en/es still processing
 *   processing   all three processing — "generating" button state
 *   failed       run failed: natural null, all tracks failed
 *   none         captions [] — feature off / never processed
 *
 * Real HLS masters carry no subtitle renditions yet, so the mock also flags
 * the video (`__mockCaptions`) for useCaptions to synthesize native
 * TextTracks with sample cues — making the picker and ::cue styling fully
 * exercisable end to end. Delete this file when the pipeline ships.
 */

const VARIANTS = {
    done: {
        natural_language: "en",
        captions: [
            { language: "pt_BR", status: "done" },
            { language: "en", status: "done" },
            { language: "es", status: "done" },
        ],
    },
    match: {
        natural_language: "pt_BR",
        captions: [
            { language: "pt_BR", status: "done" },
            { language: "en", status: "done" },
            { language: "es", status: "done" },
        ],
    },
    partial: {
        natural_language: "pt_BR",
        captions: [
            { language: "pt_BR", status: "done" },
            { language: "en", status: "processing" },
            { language: "es", status: "processing" },
        ],
    },
    processing: {
        natural_language: null,
        captions: [
            { language: "pt_BR", status: "processing" },
            { language: "en", status: "processing" },
            { language: "es", status: "processing" },
        ],
    },
    failed: {
        natural_language: null,
        captions: [
            { language: "pt_BR", status: "failed" },
            { language: "en", status: "failed" },
            { language: "es", status: "failed" },
        ],
    },
    none: {
        natural_language: null,
        captions: [],
    },
};

const SAMPLE_CUE_LINES = {
    pt_BR: [
        "Bem-vindo à aula — este é um exemplo de legenda.",
        "As legendas são geradas automaticamente em três idiomas.",
        "Você pode trocar o idioma no menu de legendas.",
        "Este texto é fictício, usado apenas para validar o player.",
    ],
    en: [
        "Welcome to the lesson — this is a sample subtitle.",
        "Captions are generated automatically in three languages.",
        "You can switch the language in the captions menu.",
        "This text is fictional, used only to validate the player.",
    ],
    es: [
        "Bienvenido a la clase — este es un subtítulo de ejemplo.",
        "Los subtítulos se generan automáticamente en tres idiomas.",
        "Puedes cambiar el idioma en el menú de subtítulos.",
        "Este texto es ficticio, usado solo para validar el player.",
    ],
};

function activeVariant() {
    const raw = new URLSearchParams(window.location.search).get("mock_captions");
    if (!raw) return null;
    if (raw === "true") return VARIANTS.done;
    return VARIANTS[raw] ?? null;
}

/**
 * Merge the mock projection into the fetched `video` object. Returns the
 * input untouched when the URL carries no `mock_captions` param.
 */
export function applyMockCaptions(video) {
    const variant = activeVariant();
    if (!variant || !video) return video;

    return {
        ...video,
        natural_language: variant.natural_language,
        captions: variant.captions,
        __mockCaptions: true,
    };
}

/**
 * Build the sample cues for one language, spanning the whole video. 4s cues
 * with a 1s gap, cycling the sample lines with a running index so seeking
 * anywhere shows movement.
 */
export function buildMockCues(language, duration) {
    const lines = SAMPLE_CUE_LINES[language] ?? SAMPLE_CUE_LINES.pt_BR;
    const total = Math.max(1, Math.floor((duration || 600) / 5));

    return Array.from({ length: total }, (_, index) => {
        const start = index * 5;
        return {
            start,
            end: start + 4,
            text: `${lines[index % lines.length]} (${index + 1})`,
        };
    });
}
